import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, ExternalLink, Settings } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import { useEventOverview, OverviewIssue, OverviewStep } from '../hooks/useEventOverview';
import { isCourseEvent } from '../lib/eventTypes';
import { PUBLIC_SITE_URL } from '../config/api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { StatusDot, StatusTone } from '../components/ui/status-dot';
import { Alert, AlertDescription } from '../components/ui/alert';
import { cn } from '../lib/utils';

// Page d'accueil d'un événement (2026-09-23).
//
// Avant, savoir ce qu'il restait à faire demandait d'ouvrir six écrans l'un
// après l'autre. Ici : les chiffres qu'on vient chercher, l'avancement de la
// préparation, et la liste de ce qui bloque, chaque ligne menant à l'écran où
// ça se règle. Tout est calculé par le serveur (GET /events/{id}/overview) :
// cette page ne décide d'aucune règle, elle les affiche.

const STATUS_TONE: Record<string, StatusTone> = {
  published: 'success',
  active: 'success',
  draft: 'neutral',
  finished: 'info',
  archived: 'neutral',
};

const daysUntil = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
};

const EventOverview: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { selectedEventId, selectedEvent } = useEvent();
  const { data, isLoading, isError, refetch } = useEventOverview(selectedEventId);

  if (!selectedEventId) {
    return (
      <div className="p-4 md:p-8">
        <p className="text-muted-foreground">{t('overview.selectEvent')}</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/admin/events')}>{t('nav.events')}</Button>
      </div>
    );
  }

  const course = isCourseEvent(selectedEvent?.event_type);
  const fmtDate = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }) : '';
  const euros = (cents: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(cents / 100);

  const ev = data?.event;
  const m = data?.metrics;
  const days = daysUntil(ev?.date_start);
  const dateRange = ev
    ? (ev.date_end && ev.date_end !== ev.date_start ? `${fmtDate(ev.date_start)} – ${fmtDate(ev.date_end)}` : fmtDate(ev.date_start))
    : '';

  // Où mène chaque étape et chaque problème : l'écran qui le règle.
  const resultsPath = `/admin/events/${selectedEventId}/${course ? 'race-results' : 'crossfit-results'}`;
  const stepPath: Record<string, string> = {
    categories: '/admin/categories',
    wods: '/admin/wods',
    registrations: '/admin/registrations',
    planning: '/admin/heats',
    results: resultsPath,
  };
  const issuePath = (issue: OverviewIssue): string => {
    switch (issue.code) {
      case 'no_categories': return '/admin/categories?create=true';
      case 'no_wods': return '/admin/wods/new';
      case 'wod_variant_missing': return issue.params.wod_id ? `/admin/wods/${issue.params.wod_id}/edit` : '/admin/wods';
      case 'wod_without_heats':
      case 'teams_unassigned': return '/admin/heats';
      default: return '/admin/overview';
    }
  };

  // Le chiffre principal de chaque étape porte le pluriel ; le vocabulaire
  // suit le type d'événement (WOD/heat en CrossFit, course/vague en course).
  const MAIN_COUNT: Record<string, string> = { categories: 'categories', wods: 'wods', registrations: 'confirmed', planning: 'heats' };
  const stepDetail = (s: OverviewStep) => {
    const variant = course && (s.key === 'wods' || s.key === 'planning') ? 'Course' : '';
    const count = MAIN_COUNT[s.key] ? s.counts[MAIN_COUNT[s.key]] ?? 0 : undefined;
    return t(`overview.stepDetail.${s.key}.${s.status}${variant}`, { ...s.counts, count, defaultValue: '' });
  };

  const stepLabel = (s: OverviewStep) =>
    s.key === 'wods' && course ? t('nav.courses') : s.key === 'planning' && course ? t('nav.waves') : t(`overview.steps.${s.key}`);

  return (
    <div className="space-y-8 p-4 md:p-8">
      {/* En-tête */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex min-w-0 flex-col gap-2">
          {isLoading ? <Skeleton className="h-4 w-56" /> : ev && (
            <StatusDot
              tone={STATUS_TONE[ev.status] ?? 'neutral'}
              className="text-sm text-muted-foreground"
              label={[
                t(`overview.status.${ev.status}`, { defaultValue: ev.status }),
                ev.registration_open
                  ? (ev.registration_end_date
                    ? t('overview.registrationsOpenUntil', { date: fmtDate(ev.registration_end_date) })
                    : t('overview.registrationsOpen'))
                  : t('overview.registrationsClosed'),
              ].join(' · ')}
            />
          )}
          <h1 className="font-anton text-4xl uppercase leading-none md:text-5xl">{selectedEvent?.name}</h1>
          {isLoading ? <Skeleton className="h-4 w-40" /> : ev && (
            <p className="text-sm text-muted-foreground">
              {[
                dateRange,
                days === null ? null
                  : days > 0 ? t('overview.inDays', { count: days })
                  : days === 0 ? t('overview.today')
                  : null,
              ].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className="ml-auto flex gap-2">
          {ev?.slug && (
            <Button variant="ghost" size="sm" asChild>
              <a href={`${PUBLIC_SITE_URL}/events/${ev.slug}`} target="_blank" rel="noopener noreferrer">
                {t('nav.viewPublicPage')}<ExternalLink size={14} />
              </a>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/admin/events/${selectedEventId}/settings`)}>
            <Settings size={16} />{t('nav.eventSettings')}
          </Button>
        </div>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-4">
            {t('overview.loadError')}
            <Button variant="outline" size="sm" onClick={() => refetch()}>{t('common.retry')}</Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Chiffres clés — en ligne, séparés par des filets, sans cartes. */}
      <div className="grid grid-cols-2 border-y border-white/[0.08] lg:grid-cols-4">
        {[
          {
            label: t('overview.metrics.registrations'),
            value: m ? <>{m.registrations_confirmed}{m.registrations_capacity != null && <span className="text-lg text-muted-foreground"> / {m.registrations_capacity}</span>}</> : null,
            foot: m && m.registrations_capacity ? (
              <span className="block h-[3px] w-full bg-white/10">
                <span className="block h-full bg-foreground/80" style={{ width: `${Math.min(100, (m.registrations_confirmed / m.registrations_capacity) * 100)}%` }} />
              </span>
            ) : null,
          },
          { label: t('overview.metrics.revenue'), value: m ? euros(m.revenue_cents) : null, foot: null },
          {
            label: course ? t('nav.waves') : t('overview.metrics.heats'),
            value: m ? m.heats_total : null,
            foot: m && m.heats_total > 0 ? <span className="text-xs text-muted-foreground">{t('overview.metrics.heatsPublished', { count: m.heats_published })}</span> : null,
          },
          { label: t('nav.volunteers'), value: m ? m.volunteers_total : null, foot: null },
        ].map((tile, i) => (
          <div key={i} className={cn('flex flex-col gap-2 py-5 pr-4', i % 2 === 1 && 'pl-5 border-l border-white/[0.08]', i >= 2 && 'lg:pl-5 lg:border-l lg:border-white/[0.08] border-t border-white/[0.08] lg:border-t-0')}>
            <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{tile.label}</span>
            {tile.value === null
              ? <Skeleton className="h-8 w-24" />
              : <span className="font-anton text-3xl leading-none">{tile.value}</span>}
            {tile.foot}
          </div>
        ))}
      </div>

      <div className="grid gap-10 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
        {/* Préparation */}
        <section aria-labelledby="ov-steps">
          <h2 id="ov-steps" className="pb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t('overview.preparation')}</h2>
          {isLoading && <div className="space-y-4 pt-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>}
          <ol className="flex flex-col">
            {data?.steps.map((s, i) => (
              <li key={s.key}>
                <Link to={stepPath[s.key] ?? '/admin/overview'} className="group flex items-center gap-4 py-3 text-foreground no-underline">
                  <span className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs',
                    s.status === 'done' && 'border-success text-success',
                    s.status === 'warning' && 'border-warning font-bold text-warning',
                    s.status === 'todo' && 'border-white/20 text-muted-foreground',
                  )}>
                    {s.status === 'done' ? <Check size={13} /> : s.status === 'warning' ? '!' : i + 1}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className={cn('text-sm', s.status === 'done' ? 'text-muted-foreground' : 'font-semibold')}>{stepLabel(s)}</span>
                    <span className="text-xs text-muted-foreground">{stepDetail(s)}</span>
                  </span>
                  <ChevronRight size={16} className="text-muted-foreground/50 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </li>
            ))}
          </ol>
        </section>

        {/* À régler */}
        <section aria-labelledby="ov-issues">
          <h2 id="ov-issues" className="pb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            {t('overview.toFix')}{data ? ` · ${data.issues.length}` : ''}
          </h2>
          {isLoading && <div className="space-y-4 pt-3">{[0, 1].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>}
          {data && data.issues.length === 0 && (
            <p className="py-4 text-sm text-muted-foreground">{t('overview.nothingToFix')}</p>
          )}
          <ul className="flex flex-col">
            {data?.issues.map((issue, i) => {
              const p = issue.params || {};
              const wod = p.wod_number != null ? t('overview.wodRef', { number: p.wod_number, name: p.wod_name ?? '' }) : (p.wod_name ?? '');
              const sample = Array.isArray(p.sample) ? p.sample.join(', ') : '';
              const more = Math.max(0, (p.count ?? 0) - (Array.isArray(p.sample) ? p.sample.length : 0));
              const title = t(`overview.issues.${issue.code}.title`, {
                ...p,
                count: p.count ?? p.teams_count,
                wod,
                categories: Array.isArray(p.categories) ? p.categories.join(', ') : p.categories,
                defaultValue: t('overview.issues.unknown', { code: issue.code }),
              });
              const detail = issue.code === 'teams_unassigned'
                ? [sample, more > 0 ? t('overview.andMore', { count: more }) : ''].filter(Boolean).join(' ')
                : t(`overview.issues.${issue.code}.detail`, { ...p, count: p.count ?? p.teams_count, defaultValue: '' });
              return (
                <li key={`${issue.code}-${i}`} className="flex items-center gap-4 border-b border-white/[0.07] py-4">
                  <span aria-hidden className={cn('h-1.5 w-1.5 shrink-0 rounded-full', issue.severity === 'warning' ? 'bg-warning' : 'bg-muted-foreground/60')} />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-sm font-semibold">{title}</span>
                    {detail && <span className="text-xs text-muted-foreground">{detail}</span>}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => navigate(issuePath(issue))}>
                    {t(`overview.issues.${issue.code}.action`, { defaultValue: t('overview.open') })}
                  </Button>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default EventOverview;
